/**
 * Bruce Lee — „Muhammad Ali vs. Bruce Lee".
 *
 * Na konci ťahu sa smie posunúť o 1 priestor. Karty JEET KUNE DO dávajú po
 * súboji akciu navyše, obranné karty ich vracajú z odhodu do ruky.
 */

import { neighbors, shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, dealDamage, discardRandom, drawCard, emit, gainAction, getMap, returnFromDiscard } from '../ops.js';
import { areEnemies } from '../players.js';
import type { PlayerIndex } from '../types.js';
import { feint, momentousShift, regroup, skirmish } from './common.js';
import { fighterOrNull } from './helpers.js';
import type { CardEffect, EffectRegistry, HeroSpecial } from './types.js';

const BRUCE = 'bruce-lee';
const JKD = 'jeet-kune-do';
const BRING = 'bruce-lee.bring-it-on';
const PUNCH = 'bruce-lee.one-inch-punch';

const bruceOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === BRUCE) ?? null;

/** Karty JEET KUNE DO — označené kľúčovým slovom, aby ich Be Like Water našiel. */
function jkd(effect: CardEffect): CardEffect {
  return {
    ...effect,
    keywords: [JKD],
    after: (e) => {
      effect.after?.(e);
      gainAction(e.ctx, e.player);
    },
  };
}

/** „Choose a JEET KUNE DO card in your discard pile and add it to your hand." */
function askJkd(ctx: Ctx, player: PlayerIndex, source: string, label: string): void {
  askChoice(ctx, {
    kind: 'card',
    player,
    source,
    label,
    optional: false,
    from: 'discard',
    purpose: 'toHand',
    keyword: JKD,
  });
}

/** „If your hand is empty, the value of this card is 6 instead. AFTER: Draw 2 cards." */
const littleDragon: CardEffect = {
  during: (e, value) => ((e.ctx.state.players[e.player]?.hand.length ?? 1) === 0 ? 6 : value),
  after: (e) => {
    drawCard(e.ctx, e.player);
    drawCard(e.ctx, e.player);
  },
};

/** „If Bruce Lee has 5 or less health, draw 3 cards." */
const tasteOfBlood: CardEffect = {
  after: (e) => {
    const bruce = bruceOf(e.ctx, e.player);
    if (bruce && bruce.hp <= 5) for (let i = 0; i < 3; i++) drawCard(e.ctx, e.player);
  },
};

/** „Choose a JEET KUNE DO card in your discard pile and add it to your hand." */
const beLikeWater: CardEffect = {
  after: (e) => askJkd(e.ctx, e.player, e.cardId, 'Be Like Water: vezmi si kartu JEET KUNE DO z odhodu'),
};

/** „Choose a JEET KUNE DO card in your discard pile and add it to your hand. Gain 1 action." */
const hooWhaaaaaa: CardEffect = {
  scheme: (e) => {
    askJkd(e.ctx, e.player, e.cardId, 'Hoo-Whaaaaaa!: vezmi si kartu JEET KUNE DO z odhodu');
    gainAction(e.ctx, e.player);
  },
};

/** „If either fighter started this turn in a different space, this card's value is 5 instead." */
const highStraightLead = jkd({
  during: (e, value) => {
    const moved = [e.fighterId, e.opponentFighterId].some((id) => {
      const f = fighterOrNull(e.ctx, id);
      const start = id ? e.ctx.state.turn.startSpaces[id] : undefined;
      return Boolean(f && start && start !== f.spaceId);
    });
    return moved ? 5 : value;
  },
});

/** „Your opponent discards 1 random card." */
const downwardSideKick = jkd({
  after: (e) => {
    if (e.opponentPlayer !== null) discardRandom(e.ctx, e.opponentPlayer, true);
  },
});

/** „Draw 1 card." */
const wristLock = jkd({ after: (e) => void drawCard(e.ctx, e.player) });

/** „IMMEDIATELY: Cancel all effects on your opponent's card." */
const interceptingFist = jkd({ cancelsOpponent: true });

/** „Deal 1 damage to the opposing fighter." */
const corkscrewFingerJab = jkd({
  after: (e) => {
    if (e.opponentFighterId) dealDamage(e.ctx, e.opponentFighterId, 1);
  },
});

/** „Bruce Lee may swap spaces with the opposing fighter." */
const shortLeadHook = jkd({
  after: (e) => {
    const bruce = fighterOrNull(e.ctx, e.fighterId);
    if (bruce?.defId !== BRUCE || !e.opponentFighterId) return;
    askChoice(e.ctx, {
      kind: 'swap',
      player: e.player,
      source: e.cardId,
      label: 'Short Lead Hook: Bruce si smie vymeniť miesto so súperom',
      optional: true,
      fighterId: bruce.id,
      candidates: [e.opponentFighterId],
    });
  },
});

/**
 * „Choose an opposing fighter in Bruce Lee's zone. Place them in any space
 * adjacent to Bruce Lee. Gain 1 action."
 */
const bringItOn: CardEffect = {
  scheme: (e) => {
    const bruce = bruceOf(e.ctx, e.player);
    if (bruce) {
      const map = getMap(e.ctx);
      const targets = e.ctx.state.fighters.filter(
        (f) => areEnemies(e.ctx.state, f.owner, e.player) && shareZone(map, f.spaceId, bruce.spaceId),
      );
      if (targets.length > 0) {
        askChoice(e.ctx, {
          kind: 'option',
          player: e.player,
          source: e.cardId,
          label: 'Bring It On: ktorú súperovu figúrku pritiahneš k Bruceovi?',
          optional: false,
          options: targets.map((f) => ({ label: f.name, follow: { kind: 'effect' as const, effect: BRING, data: f.id } })),
        });
      }
    }
    gainAction(e.ctx, e.player);
  },
  onFollow: (ctx, choice, targetId) => {
    const bruce = bruceOf(ctx, choice.player);
    if (!bruce || !fighterOrNull(ctx, targetId)) return;
    askChoice(ctx, {
      kind: 'place',
      player: choice.player,
      source: choice.source,
      label: 'Bring It On: polož súperovu figúrku vedľa Brucea',
      optional: false,
      fighterId: targetId,
      spaces: neighbors(getMap(ctx), bruce.spaceId).filter((id) => !ctx.state.fighters.some((f) => f.spaceId === id)),
    });
  },
};

/**
 * „Deal 2 damage to an adjacent fighter. If this defeats that fighter, return
 * this card to your hand instead of discarding it." Schéma je v odhode, keď sa
 * cieľ vyberá — pri porážke sa z neho vráti.
 */
const oneInchPunch: CardEffect = {
  scheme: (e) => {
    const bruce = bruceOf(e.ctx, e.player);
    if (!bruce) return;
    const around = new Set(neighbors(getMap(e.ctx), bruce.spaceId));
    const targets = e.ctx.state.fighters.filter((f) => around.has(f.spaceId));
    if (targets.length === 0) return;
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'One-Inch Punch: 2 poškodenia susednej figúrke',
      optional: false,
      options: targets.map((f) => ({ label: f.name, follow: { kind: 'effect' as const, effect: PUNCH, data: f.id } })),
    });
  },
  onFollow: (ctx, choice, targetId) => {
    if (!fighterOrNull(ctx, targetId)) return;
    dealDamage(ctx, targetId, 2);
    if (!fighterOrNull(ctx, targetId) && choice.source) returnFromDiscard(ctx, choice.player, [choice.source]);
  },
};

/** „All of Bruce Lee's attacks this turn are +1 value. Gain 1 action." */
const nunchaku: CardEffect = {
  scheme: (e) => {
    const buffs = (e.ctx.state.turn.buffs ??= {});
    buffs.attack = (buffs.attack ?? 0) + 1;
    emit(e.ctx, { type: 'specialUsed', player: e.player, heroId: BRUCE, name: 'Nunchaku: útoky Brucea tento ťah +1' });
    gainAction(e.ctx, e.player);
  },
};

export const bruceLeeCards: EffectRegistry = {
  'bruce-lee.skirmish': skirmish(2),
  'bruce-lee.little-dragon': littleDragon,
  'bruce-lee.taste-of-blood': tasteOfBlood,
  'bruce-lee.be-like-water': beLikeWater,
  'bruce-lee.hoo-whaaaaaa': hooWhaaaaaa,
  'bruce-lee.jeet-kune-do-high-straight-lead': highStraightLead,
  'bruce-lee.jeet-kune-do-downward-side-kick': downwardSideKick,
  'bruce-lee.jeet-kune-do-wrist-lock': wristLock,
  'bruce-lee.jeet-kune-do-intercepting-fist': interceptingFist,
  'bruce-lee.jeet-kune-do-corkscrew-finger-jab': corkscrewFingerJab,
  'bruce-lee.jeet-kune-do-short-lead-hook': shortLeadHook,
  [BRING]: bringItOn,
  [PUNCH]: oneInchPunch,
  'bruce-lee.nunchaku': nunchaku,
  'bruce-lee.momentous-shift': momentousShift,
  'bruce-lee.feint': feint,
  'bruce-lee.regroup': regroup,
};

export const bruceLeeSpecial: HeroSpecial = {
  name: 'Be Water',
  /** „At the end of your turn, you may move Bruce Lee 1 space." */
  endOfTurn: (ctx, player) => {
    const bruce = bruceOf(ctx, player);
    if (!bruce) return;
    askChoice(ctx, {
      kind: 'move',
      player,
      source: null,
      label: 'Koniec ťahu: Bruce Lee sa smie posunúť o 1',
      optional: true,
      fighters: [bruce.id],
      max: 1,
      through: false,
    });
  },
  /** Nunchaku: +1 ku všetkým útokom Brucea v tomto ťahu. */
  attackModifier: (ctx, player, fighterId) =>
    ctx.state.turn.player === player && fighterOrNull(ctx, fighterId)?.defId === BRUCE ? (ctx.state.turn.buffs?.attack ?? 0) : 0,
};
